import classNames from 'classnames';
import { FC, useEffect } from 'react';

import { GetInvoicingAccountResponse } from '@amzn/coral_com-amazon-asxcustomerprofileservice';
import { EntitlementViewBase, ProductViewBase } from '@amzn/coral_com-amazon-sssapinterface-model-structures-credits';
import { KatProgress } from '@amzn/katal-react';
import { useBundle } from '@amzn/react-arb-tools';
import {
  AsxImage,
  FlatCard,
  Heading,
  HEADING_SIZE,
  HEADING_TAG,
  Icon,
  ICON_NAME,
  useApi,
} from '@amzn/sss-website-theme';

import { useAcpsCreditsClient } from 'src/clients/acps';
import { CHECKOUT_BUNDLE_NAME, COMMON_BUNDLE_NAME } from 'src/constants/panther/resource-bundles';
import { STAGE } from 'src/enums/Stage';
import { t } from 'src/helpers';
import { getCurrentStage } from 'src/helpers/stage/stage-helpers';
import fallbackImage3 from 'src/images/fallback-image-3.png';

import styles from './CheckoutStep4.module.scss';

interface CheckoutStep4Props {
  product: ProductViewBase | null | undefined;
  entitlement: EntitlementViewBase | null | undefined;
}

export const CheckoutStep4: FC<CheckoutStep4Props> = ({ product, entitlement }) => {
  const [checkoutBundle] = useBundle(CHECKOUT_BUNDLE_NAME);
  const [commonBundle] = useBundle(COMMON_BUNDLE_NAME);

  const { getInvoicingAccount } = useAcpsCreditsClient();

  const { response: invoicingAccountResponse, callApi: fetchInvoicingAccount } = useApi<GetInvoicingAccountResponse>();

  useEffect(() => {
    fetchInvoicingAccount(getInvoicingAccount);
  }, [fetchInvoicingAccount, getInvoicingAccount]);

  if (!product || !entitlement) {
    return (
      <div role="status">
        <KatProgress katAriaLabel={t(commonBundle, 'loading_aria_label')} indeterminate type="circular" />
      </div>
    );
  }

  const requestId = entitlement.entitlementId;
  const contactEmail = 'amazon-carbon-credits-us@amazon.com';

  // Use environment stage to show fallback account/routing numbers in non prod stages
  const isNonProdStage = getCurrentStage() !== STAGE.PROD;
  const routingNumber =
    invoicingAccountResponse?.wireTransferRoutingNumber || (isNonProdStage ? 'MOCK_ROUTING_NUMBER' : undefined);
  const vban =
    invoicingAccountResponse?.virtualBankAccountNumber || (isNonProdStage ? 'MOCK_ACCOUNT_NUMBER' : undefined);

  return (
    <section className={styles.checkoutStep4}>
      <div className={classNames(styles.card, styles.firstCard)} data-testid="submit-payment-template-first-card">
        {/* This image is not just a fallback, but rather purposefully decided per design */}
        <AsxImage className={styles.image} src={fallbackImage3} componentName="CheckoutStep4" />

        <div className={styles.messageWrapper}>
          <div className={styles.iconContainer}>
            <Icon className={styles.icon} name={ICON_NAME.CHECK} />
          </div>

          <Heading className={styles.message} as={HEADING_TAG.H4} size={HEADING_SIZE.h4}>
            {t(checkoutBundle, 'checkout_step_4_first_card_thankness_message')}
          </Heading>
        </div>
      </div>
      <FlatCard
        className={classNames(styles.card, styles.secondCard)}
        title={t(checkoutBundle, 'checkout_step_4_second_card_title')}
        tagline={t(checkoutBundle, 'checkout_request_id_tagline', {
          requestId,
        })}
        data-testid="submit-payment-template-second-card"
      >
        <ol className={styles.secondCardContent}>
          <li className={styles.item}>
            <div className={styles.itemNumber}>{t(checkoutBundle, 'checkout_step_4_body_item_01_label')}</div>
            <div className={styles.itemWithBanner}>
              <p className={styles.subtitle}>{t(checkoutBundle, 'checkout_step_4_body_item_01_subtitle')}</p>
              <p className={styles.paragraph}>
                {t(checkoutBundle, 'checkout_step_4_body_item_01_copy')}{' '}
                <a href={`mailto:${contactEmail}`} className={styles.mailtoLink}>
                  {contactEmail}
                </a>
              </p>
            </div>
          </li>

          <li className={styles.item}>
            <div className={styles.itemHeader}>
              <div className={styles.itemNumber}>{t(checkoutBundle, 'checkout_step_4_body_item_02_label')}</div>
              <div className={styles.itemWithBanner}>
                <p className={styles.paragraph}>{t(checkoutBundle, 'checkout_step_4_body_item_02_copy')}</p>
                {routingNumber && (
                  <Heading as={HEADING_TAG.H5} size={HEADING_SIZE.h5} className={styles.highlight}>
                    <p className={styles.headingLabel}>{t(checkoutBundle, 'checkout_step_4_routing_number_label')}</p>
                    {routingNumber}
                  </Heading>
                )}

                {vban && (
                  <Heading as={HEADING_TAG.H5} size={HEADING_SIZE.h5} className={styles.highlight}>
                    <p className={styles.headingLabel}>{t(checkoutBundle, 'checkout_step_4_account_number_label')}</p>
                    {vban}
                  </Heading>
                )}
              </div>
            </div>
          </li>

          <li className={styles.item}>
            <div className={styles.itemNumber}>{t(checkoutBundle, 'checkout_step_4_body_item_03_label')}</div>
            <p className={styles.paragraph}>{t(checkoutBundle, 'checkout_step_4_body_item_03_copy')}</p>
          </li>
        </ol>
      </FlatCard>
    </section>
  );
};
